import { useTranslation } from 'react-i18next';
import { Chip, CloseButton, Modal, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { SalesTransaction } from '@/api/models';
import { formatInLanguage, getNodeTestIdProp } from '@/utils';
import {
    formatTransactionId,
    getDominantPaymentType,
    PAYMENT_CHIP_COLOR,
    getStatusBadgeKind,
    PAYMENT_TYPE_ICON,
    STATUS_BADGE_LABEL_KEY,
} from './transactionsMeta';
import { TransactionDetailStats } from './TransactionDetailStats';

type TransactionDetailHeaderProps = {
    transaction: SalesTransaction;
    onClose: () => void;
};
export const TransactionDetailHeader = ({
    transaction,
    onClose,
}: TransactionDetailHeaderProps) => {
    const { t, i18n } = useTranslation('pages');
    const status = getStatusBadgeKind(transaction);
    const payment = getDominantPaymentType(transaction);
    return (
        <Modal.Header
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('transaction-detail-header')}
        >
            <div
                className="flex items-start justify-between gap-3"
                {...getNodeTestIdProp('transaction-detail-header-row')}
            >
                <div className="flex min-w-0 items-center gap-3">
                    <Chip
                        color={
                            payment ? PAYMENT_CHIP_COLOR[payment] : 'default'
                        }
                        variant="soft"
                        className="size-11 shrink-0 justify-center"
                    >
                        <Icon
                            aria-hidden="true"
                            className="size-5"
                            icon={
                                payment
                                    ? PAYMENT_TYPE_ICON[payment]
                                    : 'solar:wallet-bold-duotone'
                            }
                        />
                    </Chip>
                    <div className="min-w-0">
                        <div className="mb-0.5 flex flex-wrap items-center gap-2">
                            <Modal.Heading
                                className="wrap-anywhere"
                                {...getNodeTestIdProp('transaction-detail-id')}
                            >
                                {formatTransactionId(transaction.id)}
                            </Modal.Heading>
                            <Chip
                                color={
                                    status === 'refund'
                                        ? 'danger'
                                        : status === 'pending'
                                          ? 'warning'
                                          : status === 'done'
                                            ? 'success'
                                            : 'default'
                                }
                                size="sm"
                                variant="soft"
                                {...getNodeTestIdProp(
                                    'transaction-detail-status',
                                )}
                            >
                                {t(STATUS_BADGE_LABEL_KEY[status])}
                            </Chip>
                        </div>
                        <Typography
                            type="body-xs"
                            color="muted"
                            {...getNodeTestIdProp(
                                'transaction-detail-datetime',
                            )}
                        >
                            {formatInLanguage(
                                transaction.transactionDate,
                                'D MMM YYYY · HH:mm',
                                i18n.language,
                            )}
                        </Typography>
                    </div>
                </div>
                <CloseButton
                    aria-label={t('sales.widgets.transactions.modal.close')}
                    onPress={onClose}
                    className="shrink-0"
                    {...getNodeTestIdProp('transaction-detail-close')}
                />
            </div>
            <TransactionDetailStats transaction={transaction} />
        </Modal.Header>
    );
};
